import React from "react";
import {
  FlightRawEventName,
  useEmitCoreFlightRawEventOnce,
} from "@b2bportal/core-flights";
import {
  IconNameEnum,
  useFlightStyles,
  useModuleBEM,
} from "@b2bportal/core-themes";
import {
  CoreFlightsComponent,
  type FlightFareSelectionFunction,
  type PlatformComponentProps,
} from "@b2bportal/core-types";
import { useDeviceType } from "@b2bportal/core-utilities";
import { TripCategory } from "@overrides/types";
import {
  AlgomerchTags,
  FareCombinationBanner,
  FlightCardDetailsLoading,
  FlightCardRecheckBaggageBanner,
  FlightCardSummary,
  FlightCardWrapper,
  FlightDetails,
  FlightDetailsDialog,
  FlightFareGrid,
  FlightFaresLoader,
  useFlightCard,
  type UseFlightCardParams,
} from "@tenant/flights";
import { Icon } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./FlightCard.module.scss";

export interface FlightCardComponentProps
  extends PlatformComponentProps,
    UseFlightCardParams {
  onFareSelect: FlightFareSelectionFunction;
  goToNextStep: () => void;
  index?: number;
}

const COMPONENT_KEY = CoreFlightsComponent.FlightCard;

// Taken mostly from the core FlightCard component, minor updates around goToNextStep and algomerch tags
export const FlightCard = React.forwardRef<
  HTMLDivElement,
  FlightCardComponentProps
>((props, forwardedRef) => {
  const {
    flight,
    isSelected,
    sliceIndex = 0,
    onFareSelect,
    goToNextStep,
    children,
    className,
  } = props;

  const styles = useFlightStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { isDesktopAndUp } = useDeviceType();

  const {
    context: {
      isFlightDetailsLoading,
      isHackerFare,
      open,
      flightCardSummary,
      fareData,
      tripCategory,
      fareShelfFilter,
      flightDetails,
      flightSlice,
      fareShelfFilterWithUpsell,
      openFlightDetailsDialog,
      hasSelfTransferLayover,
      flightFareTrips,
    },
    handlers: {
      handleFlightCardClick,
      setOpenFlightDetailsDialog,
      handleFareGridSelection,
    },
  } = useFlightCard(props);

  useEmitCoreFlightRawEventOnce({ name: FlightRawEventName.ViewedSlice }, open);

  return (
    <>
      {/* algomerch tags need to live outside of the flight card wrapper to allow for overlay */}
      {flightCardSummary?.tags.length > 0 && !isDesktopAndUp && (
        <AlgomerchTags
          className={cn("flight-tags")}
          tags={flightCardSummary.tags}
          tagsLimit={flightCardSummary.tagsLimit}
        />
      )}
      <div className={clsx(block, className)} ref={forwardedRef}>
        <FlightCardWrapper
          className={cn("button")}
          onClick={handleFlightCardClick}
        >
          {hasSelfTransferLayover ? (
            <FlightCardRecheckBaggageBanner
              className={cn("vi-banner")}
              flightSlice={flightSlice}
            />
          ) : null}
          {flightCardSummary == null ? null : (
            <div className={cn("summary")}>
              <FlightCardSummary {...flightCardSummary} />
            </div>
          )}
          <div className={cn("fare-grid")}>
            <FlightFareGrid
              fares={fareData}
              tripType={tripCategory}
              onFareGridClick={handleFareGridSelection}
              fareShelfFilter={fareShelfFilter}
            />
          </div>
          <Icon
            iconName={IconNameEnum.dropdownTriangle}
            className={clsx(
              cn("icon", {
                expanded: isSelected,
              })
            )}
          />
        </FlightCardWrapper>

        {open && (
          <>
            <div
              className={cn("flight-card-details", {
                open: open,
                loading: isFlightDetailsLoading,
              })}
            >
              {/* Conditional rendering for flight details based on loading state */}
              {isFlightDetailsLoading ||
              flightDetails == null ||
              flightSlice == null ? (
                <FlightCardDetailsLoading />
              ) : (
                <>
                  <div className={cn("flight-details")}>
                    <FlightDetails
                      flightDetails={flightDetails}
                      flightSlice={flightSlice}
                      planeInfo=""
                      isOutgoing={sliceIndex === 0}
                    />
                    {isHackerFare && (
                      <FareCombinationBanner
                        className={cn("fare-combination")}
                      />
                    )}
                  </div>
                  <div className={cn("fare-details")}>
                    <FlightFaresLoader
                      trip={flightDetails}
                      fareTrips={flightFareTrips}
                      sliceIndex={sliceIndex}
                      fareShelfFilter={fareShelfFilterWithUpsell}
                      onFareSelect={(params) => {
                        onFareSelect({
                          ...params,
                          tripId: flightDetails.id,
                          sliceId: flight.sliceId,
                        });

                        if (isDesktopAndUp) {
                          goToNextStep();
                        } else {
                          setOpenFlightDetailsDialog(true);
                        }
                      }}
                    />
                  </div>
                </>
              )}
            </div>
          </>
        )}
        <FlightDetailsDialog
          open={openFlightDetailsDialog}
          onClose={() => setOpenFlightDetailsDialog(false)}
          onClick={() => {
            goToNextStep();
          }}
          flightSlice={flightSlice}
          isOutbound={
            sliceIndex === 0 || tripCategory === TripCategory.MULTI_CITY
          }
        />

        {children}
      </div>
    </>
  );
});
